import { FileDownloadOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React, { useId, useMemo, useState } from "react";

import { useNotification } from "../../contexts/NotificationContext";
import { ExportFieldOption, defaultFieldKeys } from "../../utils/export/fields";
import { TABLE_FORMAT_LABELS, TableFormat } from "../../utils/export/table";

// How far a long export has come: rows read of rows to read
export type ExportProgress = (done: number, total: number) => void;

interface ExportFieldsButtonProps {
  // The view's catalog of fields
  fields: readonly ExportFieldOption[];
  // Where the browser keeps the user's last pick for this catalog
  storageKey: string;
  // The dialog's title: "Export organizations"
  title: string;
  // What is exported: "The 50 results on this page"
  note?: string;
  // Writes the file of the fields picked; may take a while and may fail
  onExport: (
    format: TableFormat,
    keys: string[],
    progress: ExportProgress,
  ) => Promise<void>;
  disabled?: boolean;
}

interface Pick {
  format: TableFormat;
  keys: string[];
}

const FORMATS: TableFormat[] = ["xlsx", "csv"];

// The last pick, its fields cut to those the catalog still has; the default
// where none is kept or the browser keeps nothing
const readPick = (
  storageKey: string,
  fields: readonly ExportFieldOption[],
): Pick => {
  const fallback = { format: "xlsx" as const, keys: defaultFieldKeys(fields) };
  try {
    const kept = JSON.parse(window.localStorage.getItem(storageKey) ?? "null");
    if (!kept || !Array.isArray(kept.keys)) {
      return fallback;
    }
    const known = new Set(fields.map((field) => field.key));
    return {
      format: FORMATS.includes(kept.format) ? kept.format : "xlsx",
      keys: kept.keys.filter((key: unknown) => known.has(String(key))),
    };
  } catch {
    return fallback;
  }
};

const keepPick = (storageKey: string, pick: Pick): void => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(pick));
  } catch {
    // A browser that keeps nothing asks again next time
  }
};

const groupHeading = {
  color: "text.secondary",
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
} as const;

/**
 * The outlined Export button of a table view, and the dialog it opens: the
 * format, then the view's fields under their headings, ticked as the user
 * last left them (by default, the catalog's own). The pick is kept per
 * catalog in the browser. While the file is written the dialog says how far
 * it has come; a failure is reported and the dialog stays open.
 */
const ExportFieldsButton: React.FC<ExportFieldsButtonProps> = ({
  fields,
  storageKey,
  title,
  note,
  onExport,
  disabled = false,
}) => {
  const { showNotification } = useNotification();
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState<Pick>(() => readPick(storageKey, fields));
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<[number, number] | null>(null);

  const groups = useMemo(() => {
    const byGroup = new Map<string, ExportFieldOption[]>();
    for (const field of fields) {
      byGroup.set(field.group, [...(byGroup.get(field.group) ?? []), field]);
    }
    return Array.from(byGroup.entries());
  }, [fields]);
  const picked = new Set(pick.keys);

  const openDialog = () => {
    setPick(readPick(storageKey, fields));
    setOpen(true);
  };

  const setKeys = (keys: string[]) => setPick((last) => ({ ...last, keys }));

  const toggle = (key: string) =>
    setKeys(
      picked.has(key)
        ? pick.keys.filter((other) => other !== key)
        : [...pick.keys, key],
    );

  const run = async () => {
    setRunning(true);
    setProgress(null);
    keepPick(storageKey, pick);
    try {
      await onExport(pick.format, pick.keys, (done, total) =>
        setProgress([done, total]),
      );
      setOpen(false);
    } catch (exc) {
      console.error(exc);
      showNotification(
        exc instanceof Error && exc.message ? exc.message : "The export failed",
        "error",
      );
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<FileDownloadOutlined />}
        aria-haspopup="dialog"
        disabled={disabled}
        onClick={openDialog}
        sx={{ textTransform: "none", flexShrink: 0 }}
      >
        Export
      </Button>
      <Dialog
        open={open}
        onClose={() => !running && setOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby={titleId}
      >
        <DialogTitle id={titleId}>{title}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {note && (
            <Typography variant="body2" color="text.secondary">
              {note}
            </Typography>
          )}
          <ToggleButtonGroup
            exclusive
            size="small"
            aria-label="Format"
            value={pick.format}
            onChange={(_, format: TableFormat | null) =>
              format && setPick((last) => ({ ...last, format }))
            }
          >
            {FORMATS.map((format) => (
              <ToggleButton
                key={format}
                value={format}
                sx={{ textTransform: "none", px: 2 }}
              >
                {TABLE_FORMAT_LABELS[format]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              Fields{" "}
              <Typography component="span" variant="body2" color="text.secondary">
                {pick.keys.length} of {fields.length}
              </Typography>
            </Typography>
            <Button size="small" onClick={() => setKeys(fields.map((f) => f.key))}>
              All
            </Button>
            <Button size="small" onClick={() => setKeys([])}>
              None
            </Button>
            <Button size="small" onClick={() => setKeys(defaultFieldKeys(fields))}>
              Default
            </Button>
          </Box>

          {groups.map(([group, members]) => (
            <Box key={group} role="group" aria-label={group}>
              <Typography sx={{ ...groupHeading, mb: 0.5 }}>{group}</Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  columnGap: 2,
                }}
              >
                {members.map((field) => (
                  <FormControlLabel
                    key={field.key}
                    sx={{ mr: 0, alignItems: "flex-start" }}
                    control={
                      <Checkbox
                        size="small"
                        checked={picked.has(field.key)}
                        onChange={() => toggle(field.key)}
                        sx={{ py: 0.5 }}
                      />
                    }
                    label={
                      <Box sx={{ pt: "5px" }}>
                        <Typography variant="body2">{field.label}</Typography>
                        {field.hint && (
                          <Typography variant="caption" color="text.secondary">
                            {field.hint}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {progress && (
            <Typography
              role="status"
              variant="body2"
              color="text.secondary"
              sx={{ mr: "auto" }}
            >
              Reading {progress[0]} of {progress[1]}…
            </Typography>
          )}
          <Button onClick={() => setOpen(false)} disabled={running}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={run}
            disabled={running || pick.keys.length === 0}
          >
            {running ? "Exporting…" : "Export"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExportFieldsButton;
